import React from 'react';
import './styles.scss';
import { Typography } from 'Components/atoms';
import { StepCircle } from 'Components/atoms/StepCircle';
import { ConditionRow } from 'Components/molecules/ConditionRow';
import { ActionRow } from 'Components/molecules/ActionRow';
import { ActionEntry, CondEntry, LogicOp } from 'Core-utils/types/rules.types';
import { useI18n } from 'Hooks/useI18n';
import {
    RULE_BUILDER_WHEN_TITLE,
    RULE_BUILDER_WHEN_SUBTITLE,
    RULE_BUILDER_BEHAVIOR_ALL,
    RULE_BUILDER_BEHAVIOR_ANY,
    RULE_BUILDER_ADD_CONDITION,
    RULE_BUILDER_THEN_TITLE,
    RULE_BUILDER_THEN_SUBTITLE,
    RULE_BUILDER_ELSE_TITLE,
    RULE_BUILDER_ELSE_SUBTITLE,
    RULE_BUILDER_OPTIONAL_LABEL,
} from 'Core-utils/constants/constants';

export interface SimpleModeProps {
    conditions: CondEntry[];
    behavior: LogicOp;
    thenActions: ActionEntry[];
    elseActions: ActionEntry[];
    onBehaviorChange: (b: LogicOp) => void;
    onCondChange: (id: string, patch: Partial<CondEntry>) => void;
    onCondRemove: (id: string) => void;
    onCondAdd: () => void;
    onThenChange: (id: string, patch: Partial<ActionEntry>) => void;
    onThenRemove: (id: string) => void;
    onThenAdd: () => void;
    onElseChange: (id: string, patch: Partial<ActionEntry>) => void;
    onElseRemove: (id: string) => void;
    onElseAdd: () => void;
}

const SimpleMode = ({
    conditions, behavior, thenActions, elseActions,
    onBehaviorChange, onCondChange, onCondRemove, onCondAdd,
    onThenChange, onThenRemove, onThenAdd: _onThenAdd,
    onElseChange, onElseRemove, onElseAdd: _onElseAdd,
}: SimpleModeProps) => {
    const { t } = useI18n();

    return (
        <div className="d-flex flex-column gap-3">
            {/* Step 1 – When */}
            <div className="rb-step-card">
                <div className="rb-step-card__header">
                    <StepCircle n={1} />
                    <div>
                        <Typography variant="body2" className="rb-step-card__title mb-0">
                            {t(RULE_BUILDER_WHEN_TITLE, 'When this happens')}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0" style={{ fontSize: 11 }}>
                            {t(RULE_BUILDER_WHEN_SUBTITLE, 'Add one or more conditions that must be true.')}
                        </Typography>
                    </div>
                    <div className="d-flex align-items-center gap-2 ms-auto">
                        <button
                            type="button"
                            className={`rb-behavior-btn ${behavior === 'and' ? 'rb-behavior-btn--active' : ''}`}
                            onClick={() => onBehaviorChange('and')}
                        >
                            {t(RULE_BUILDER_BEHAVIOR_ALL, 'All conditions (AND)')}
                        </button>
                        <button
                            type="button"
                            className={`rb-behavior-btn ${behavior === 'or' ? 'rb-behavior-btn--active' : ''}`}
                            onClick={() => onBehaviorChange('or')}
                        >
                            {t(RULE_BUILDER_BEHAVIOR_ANY, 'Any condition (OR)')}
                        </button>
                    </div>
                </div>
                <div className="rb-step-card__body">
                    <div className="d-flex flex-column gap-2">
                        {conditions.map((c) => (
                            <ConditionRow
                                key={c.id}
                                cond={c}
                                onChange={(patch) => onCondChange(c.id, patch)}
                                onRemove={() => onCondRemove(c.id)}
                            />
                        ))}
                    </div>
                    <button type="button" className="rb-add-link mt-3" onClick={onCondAdd}>
                        {t(RULE_BUILDER_ADD_CONDITION, '+ Add another condition')}
                    </button>
                </div>
            </div>

            {/* Step 2 – Then */}
            <div className="rb-step-card">
                <div className="rb-step-card__header">
                    <StepCircle n={2} />
                    <div>
                        <Typography variant="body2" className="rb-step-card__title mb-0">
                            {t(RULE_BUILDER_THEN_TITLE, 'Then do this')}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0" style={{ fontSize: 11 }}>
                            {t(RULE_BUILDER_THEN_SUBTITLE, 'Choose what should happen when the conditions are met.')}
                        </Typography>
                    </div>
                </div>
                <div className="rb-step-card__body">
                    <div className="d-flex flex-column gap-2">
                        {thenActions.map((a) => (
                            <ActionRow
                                key={a.id}
                                entry={a}
                                label="Then"
                                onChange={(p) => onThenChange(a.id, p)}
                                onRemove={() => onThenRemove(a.id)}
                                canRemove={thenActions.length > 1}
                            />
                        ))}
                    </div>
                </div>
            </div>

            {/* Step 3 – Otherwise */}
            <div className="rb-step-card rb-step-card--optional">
                <div className="rb-step-card__header">
                    <StepCircle n={3} />
                    <div>
                        <Typography variant="body2" className="rb-step-card__title mb-0">
                            {t(RULE_BUILDER_ELSE_TITLE, 'Otherwise do this')} <span className="rb-step-card__optional-label">({t(RULE_BUILDER_OPTIONAL_LABEL, 'optional')})</span>
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0" style={{ fontSize: 11 }}>
                            {t(RULE_BUILDER_ELSE_SUBTITLE, 'Choose what should happen when the conditions are not met.')}
                        </Typography>
                    </div>
                </div>
                <div className="rb-step-card__body">
                    <div className="d-flex flex-column gap-2">
                        {elseActions.map((a) => (
                            <ActionRow
                                key={a.id}
                                entry={a}
                                label="Otherwise"
                                onChange={(p) => onElseChange(a.id, p)}
                                onRemove={() => onElseRemove(a.id)}
                                canRemove={elseActions.length > 1}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SimpleMode;
